<script setup>
/*
	README:
	** Initially developed for DashboardHeaderUserDropdown, now for form inputs only. **

	https://headlessui.com/v1/vue/listbox

	Expected data format:{
			'id': Number, //ID for record
			'text': String, //Text displayed
			'disabled': Boolean, // True to disable and prevent clicking
			'class': '', // Custom CSS to add to item
			'itemIcon': Boolean || String, // Boolean: true to use item-icon slot (allows using a single SVG that can be styled to change colour)
													 // String: Src url to img
			...additional properties that will be accessible in change handler function
		}


	Emits:
		onItemSelected - Called when value changes. Passes selected item object.

	Slots:
	item-icon

	Notes:
		Set a fixed width around the listbox for truncated text to work for inline selected value

	Examples:
		Facility Types as ListBox:
			facility_types = ref([{ id: '', text: '', disabled: false, itemIcon: false, action: '' }]),
			facility_type = ref({ id: '', text: '', disabled: false, itemIcon: false, action: '' }),

		<list-box
			:inlineLabelIcon=false
			label="Facility Type"
			label-text-size="text-sm"
			:items="facility_types"
			v-model="facility_type"
			:showSelectedOutline="false"
			:showSelectedCheckmark="true"
			:showOptionsOverContent="true"
			:showOptionsOnTop="false"
			:showListBoxOutline="true"
			:wrapItemText = "true">
		</list-box>
 */

import { computed, defineModel } from 'vue'
import {
	Listbox,
	ListboxButton,
	ListboxOptions,
	ListboxOption,
	ListboxLabel,
} from '@headlessui/vue'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid'
import ValidationError from '@/components/Forms/InputError.vue'
import { ApiGatewayResponse } from '@/api/ApiGateway.js'
import { Float } from '@headlessui-float/vue'

const emit = defineEmits(['onItemSelected'])

const props = defineProps({
	inlineLabel: { type: String, required: false }, //Text shown in listbox instead of selected value, omit to show selected value
	inlineLabelIcon: { type: Boolean, required: false },// True to indicate that the listbox has an inline label icon (use inline-label-icon slot for svg)
	label: { type: String, default: '' }, // Label shown above ListBox
	labelTextSize: { type: String, default: '' },// CSS Class to change size of label text
	labelClass: { type: String, default: '' },// CSS classes for additional label sizing
	items: { type: Array, default: () => [] },// Array of items (see data format defined above)
	itemClass: { type: String, default: '' },// CSS Classes to style item text
	disabled: { type: Boolean, default: false },//True to disable listbox
	showListBoxOutline: { type: Boolean, required: true },//True to show outline around listbox
	showSelectedOutline: { type: Boolean, default: false },//True to show outline around selected item
	showSelectedCheckmark: { type: Boolean, default: false },//True to show checkmark next to selected item
	wrapItemText: { type: Boolean, default: true }, //Set false to force listbox to grow
	showOptionsOverContent: { type: Boolean, default: true }, //True: Options will float over other content, false inline and push down content under it
	showOptionsOnTop: { type: Boolean, default: false }, //Show list of items above input
	showOptionsBorder: { type: Boolean, default: false }, //Show a border around the listbox options when inline
	showOptionsOutsideModal : { type: Boolean, default: false },
	maxSearchResultsHeight: { type: String, default: 'max-h-60' },
	errors: { type: ApiGatewayResponse, default: null },
	errorField: { type: String, default: null },
	flipItems: { type: Boolean, default: false }, //True to move items above input when they don't fit
});

const labelCustomClass = computed(() =>
{
	return [
		props.labelClass,
		props.labelTextSize,
		props.disabled ? 'text-disabled' : ''
	].join(' ')
})

const selectedItem = defineModel({ default: { id: '', text: '', disabled: false, itemIcon: false, action: '' } })

function hasError()
{
	return props.errors?.validationErrors()?.find((error) => error.source === props.errorField) != null
}

const listBoxCustomClass = computed(() =>
{
	const classes = [];
	if (props.showListBoxOutline)
	{
		classes.push('border ');
		if (!props.disabled)
		{
			classes.push('group-hover/outer:ring-1 group-hover/outer:ring-primary-bold-hover group-hover/outer:border-primary-bold-hover');
		}
		classes.push(hasError() ? 'border-error' : 'border-neutral');
	}
	else if (hasError())
	{
		classes.push('border border-error')
	}

	return classes.join(' ')
})

function isItemSelected(p_item)
{
	if ((selectedItem.value === null || selectedItem.value.id === null) && (p_item.id === 0 || p_item.id === null))
	{
		return true;
	}

	return p_item.id === selectedItem.value.id
}

/**
 * Uses the id to get the text from the Items array and updates the text in the selectedItem (model) if missing or incorrect
 * @returns {string}
 */
function getSelectedItemText()
{
	for (let i = 0; i < props.items.length; i++)
	{
		if (props.items[i].id === selectedItem.value.id)
		{
			selectedItem.value.text = props.items[i].text;

			return (props.items[i].text) ? props.items[i].text : '';
		}
	}
	return ''
}
</script>

<template>
	<div class="flex flex-col relative mb-4">

		<Listbox v-model="selectedItem" :disabled="props.disabled" @update:modelValue="value => emit('onItemSelected', value)">

			<ListboxLabel v-if="props.label" class="leading-6 mb-2 block text-label" :class="labelCustomClass">{{ props.label }}</ListboxLabel>
			<div class="group/outer relative">
				<Float
					:portal="showOptionsOutsideModal"
					adaptiveWidth
					:flip="flipItems"
					placement="bottom-start"
				>
					<ListboxButton
						class="h-11 block w-full font-medium rounded bg-neutral pl-2 py-2 pr-2 text-label outline-none placeholder:text-placeholder "
						:class="listBoxCustomClass"
					>
						<!--focus:outline-none focus-visible:outline-indigo-500 focus-visible:ring-2 focus-visible:ring-neutral/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300-->
						<template #default="{ open }">
							<div class="group/inner flex flex-row justify-between w-full ">
								<div v-if="props.inlineLabelIcon" class="group-hover/inner:fill-icon-primary-hover min-h-5 mt-[1px]">
									<slot name="inline-label-icon"></slot>
	<!--								<img :src="props.inlineLabelIcon" class="min-h-5 min-w-5 inline-block"/>-->
								</div>
								<div v-if="props.inlineLabel && (getSelectedItemText().length === 0)"
									  :class="[
										  'grow truncate text-label text-left',
										  props.inlineLabelIcon ? 'ml-4 ' : 'ml-2',
									 ]"
								>
									{{ props.inlineLabel }}
								</div>
								<div v-else class="mx-2 max-h-6 truncate" :class="[ props.disabled ? 'text-disabled' : 'text-label']">
									{{ getSelectedItemText() }}
								</div>
								<div v-if="!props.disabled">
									<ChevronUpIcon v-if="open" class="h-5 w-5 text-icon-neutral mt-1 group-hover/outer:fill-icon-primary-dark " aria-hidden="true" />
									<ChevronDownIcon v-else class="h-5 w-5 text-icon-neutral mt-1 group-hover/outer:fill-icon-primary-dark " aria-hidden="true" />
								</div>
							</div>
						</template>

					</ListboxButton>
					<ListboxOptions
						:class="[
							'mt-1 space-y-1 w-full',
							props.showOptionsBorder ? 'ring-1 ring-primary-bold-hover border border-primary-bold-hover focus:outline-none' : '',
							props.showOptionsOnTop ? 'bottom-full' : '',
							!props.showOptionsOutsideModal ? 'absolute z-10 max-h-60 w-full overflow-auto rounded bg-neutral py-1 mb-2 text-base shadow-lg ring-1 ring-primary-bold-hover border border-primary-bold-hover focus:outline-none  sm:text-sm' : '!mt-0',
							props.showOptionsOutsideModal ? ' overflow-auto rounded bg-neutral py-1 mb-2 text-base shadow-lg ring-1 ring-primary-bold-hover border border-primary-bold-hover focus:outline-none  sm:text-sm' : '!mt-0',
							maxSearchResultsHeight
							]">
						<ListboxOption
							v-for="item in props.items"
							:key="item.id"
							:value="item"
							:disabled="item.disabled"
							class="w-full"
							:class="[props.itemClass, item.class ? item.class : '']"
							as="template"
						>

							<li style="outline-width:1px" :class="[
									'group/option flex flex-row justify-between relative select-none py-2 pr-4',
									item.disabled ? 'opacity-70 !cursor-not-allowed' : 'cursor-pointer',
									isItemSelected(item) ? 'bg-primary-subtle outline-none' : 'text-label',
									isItemSelected(item) && props.showSelectedOutline && !props.disabled ? 'rounded border border-primary-bold-hover' : '',
									props.inlineLabelIcon ? 'pl-11.5' : 'pl-4.5'
								 ]">

								<div v-if="item.itemIcon === true" class="mr-2 pt-1 max-w-5">
									<slot name="item-icon"></slot>
								</div>
								<div v-if="typeof item.itemIcon === 'string' && item.itemIcon.length > 0" class="mr-2 max-w-5">
									<img :src="item.itemIcon" alt="Item Icon" />
								</div>
								<div :class="[
									  isItemSelected(item) ? 'font-medium text-label' : 'font-normal text-placeholder group-hover/option:text-label',
									  'flex-1 inline-block',
									  props.wrapItemText ? '' : 'whitespace-nowrap truncate'
									]">
									{{ item.text.length === 0 ? "&nbsp;" : item.text }}<!-- space needed to have blanks display full row height -->
								</div>
								<div
									v-show="showSelectedCheckmark && isItemSelected(item) "
									>
									<CheckIcon class="h-4 w-4 mt-1 ml-1 text-icon-neutral" aria-hidden="true" />
								</div>

							</li>

						</ListboxOption>
					</ListboxOptions>
				</Float>
			</div>
		</Listbox>
		<!-- TODO: ListBox - Test error messaging -->
		<ValidationError :errors="props.errors" :field="props.errorField" class="mt-2 ml-3.5" />
	</div>
</template>

<style scoped>

</style>